import React from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Divider,
  List,
  ListItem,
  Stack,
  alpha,
  useTheme,
  Avatar,
} from '@mui/material';
import { CheckCircleOutline, DescriptionOutlined, VisibilityOutlined } from '@mui/icons-material';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import moment from 'moment';

export default function PersonnelActivity({ resultList = [], resultLoading }) {
  const theme = useTheme();

  // Current Year Logic
  const currentYear = new Date().getFullYear();

  // FILTER LOGIC:
  // 1. Status is PUBLISHED
  // 2. Year is Current Year
  // 3. reportType is NOT QAQC
  const publishedReports = resultList
    .filter(
      (r) =>
        r.reportStatus === 'PUBLISHED' && r.reportYear === currentYear && r.reportType !== 'QAQC'
    )
    .sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate)); // Latest first

  if (resultLoading) {
    return (
      <Card sx={{ p: 3, borderRadius: '24px' }}>
        <LoaderSkeleton />
        <Divider sx={{ my: 2 }} />
        <LoaderSkeleton />
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '24px',
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        bgcolor: '#ffffff',
        boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* HEADER SECTION */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <Box
          sx={{
            p: 1,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: 'success.main',
            display: 'flex',
          }}
        >
          <CheckCircleOutline fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Published Reports
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Cycle: {currentYear} · Completed Records
          </Typography>
        </Box>
      </Stack>

      <List disablePadding sx={{ flexGrow: 1 }}>
        {publishedReports.length > 0 ? (
          publishedReports.map((report) => (
            <ListItem
              key={report.reportId}
              disableGutters
              sx={{
                mb: 2,
                p: 2,
                borderRadius: '16px',
                bgcolor: alpha(theme.palette.success.main, 0.01),
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: alpha(theme.palette.success.main, 0.3),
                  bgcolor: alpha(theme.palette.success.main, 0.02),
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    width: 40,
                    height: 40,
                  }}
                >
                  <DescriptionOutlined fontSize="small" />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary" noWrap>
                    {report.reportTitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Published: {moment(report.entryDate).format('MMM DD, YYYY')}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<VisibilityOutlined sx={{ fontSize: '14px !important' }} />}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'success.main',
                      color: 'success.main',
                    },
                  }}
                >
                  View
                </Button>
              </Stack>
            </ListItem>
          ))
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 2,
              bgcolor: alpha(theme.palette.action.disabledBackground, 0.03),
              borderRadius: '16px',
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              No reports published in {currentYear}.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Completed operational reports will appear here once approved and published.
            </Typography>
          </Box>
        )}
      </List>

      {/* FOOTER */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Button
          fullWidth
          variant="text"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.05) },
          }}
        >
          Access Compliance Vault
        </Button>
      </Box>
    </Card>
  );
}
